import { useSession } from '../lib/session'
import { useApprovals, useRefreshingMutation, useSentBack, useToday, useTodays, useToggleTask } from '../lib/queries'
import { api } from '../lib/api'
import { ReadyCard } from '../components/ReadyCard'
import { ApprovalsCard } from '../components/ApprovalsCard'
import { KidStatusCard } from '../components/KidStatusCard'
import { SentBackCard } from '../components/SentBackCard'
import { TaskList } from '../components/TaskList'
import { Empty, ErrorText, Loading, Pill } from '../components/ui'

export function Today() {
  const { viewedMemberId, viewedMember, session, isParent, members, setViewedMemberId } = useSession()
  // A parent's own day has no Ready for Tomorrow or chore list — those are kid
  // rules — so their home is each kid's day instead.
  const parentHome = isParent && viewedMemberId === session?.memberId
  const kids = parentHome ? members.filter((m) => m.role === 'kid' && m.active) : []
  const kidDays = useTodays(kids.map((kid) => kid.id))
  const { data, isLoading, error } = useToday(viewedMemberId)
  const toggle = useToggleTask(viewedMemberId, isParent)
  const approvals = useApprovals(parentHome)
  const sentBackToday = useSentBack(parentHome)
  const approve = useRefreshingMutation((completionId: string) => api.approve(completionId))
  const sendBack = useRefreshingMutation((completionId: string) => api.sendBack(completionId))

  if (isLoading) return <Loading />
  if (error) {
    return (
      <div className="page">
        <ErrorText error={error} />
      </div>
    )
  }
  if (!data) return <Empty title="Nothing to show yet" />

  const dailyDone = data.personalDaily.filter((t) => t.approved).length
  const firstName = viewedMember?.name ?? session?.name ?? ''
  const contributionDone = data.householdDaily.some((t) => t.approved)
  const contributionWaiting = !contributionDone && data.householdDaily.some((t) => t.done)

  return (
    <div className="page">
      {parentHome ? (
        <>
          <h1 className="title">The kids today</h1>
          <ApprovalsCard
            pending={approvals.data ?? []}
            busy={approve.isPending || sendBack.isPending}
            onApprove={(item) => approve.mutate(item.completionId)}
            onSendBack={(item) => sendBack.mutate(item.completionId)}
          />
          <ErrorText error={approvals.error ?? sentBackToday.error ?? sendBack.error} />
          <SentBackCard sentBack={sentBackToday.data ?? []} />
          {kids.length === 0 ? <Empty title="No kids in this household yet" /> : null}
          {kidDays.map((query, index) =>
            query.data ? (
              <KidStatusCard
                key={query.data.member.id}
                day={query.data}
                onOpen={() => setViewedMemberId(query.data.member.id)}
                onApprove={(completionId) => approve.mutate(completionId)}
                busy={approve.isPending}
                viewerId={session?.memberId ?? null}
              />
            ) : query.error ? (
              <ErrorText key={kids[index]?.id} error={query.error} />
            ) : (
              <Loading key={kids[index]?.id} />
            ),
          )}
        </>
      ) : (
        <>
          <div className="row-between">
            <h1 className="title">{firstName}’s day</h1>
            <Pill tone="accent">{data.pointsThisWeek} pts this week</Pill>
          </div>

          <ReadyCard ready={data.ready} />

          <section className="card">
            <div className="row-between">
              <h2 className="title">Daily responsibilities</h2>
              <Pill tone={dailyDone === data.personalDaily.length ? 'ok' : 'default'}>
                {dailyDone}/{data.personalDaily.length}
              </Pill>
            </div>
            <TaskList
              tasks={data.personalDaily}
              onToggle={(task, next) => toggle.mutate({ taskId: task.id, done: next })}
              byParent={isParent}
              viewerId={session?.memberId ?? null}
            />
          </section>
        </>
      )}

      <section className="card">
        <div className="row-between">
          <h2 className="title">Household contribution</h2>
          {contributionDone ? (
            <Pill tone="ok">Done</Pill>
          ) : contributionWaiting ? (
            <Pill tone="warn">Waiting</Pill>
          ) : (
            <Pill tone="warn">Pick one</Pill>
          )}
        </div>
        <p className="subtitle">One of these each day.</p>
        <TaskList
          tasks={data.householdDaily}
          showClaimant
          onToggle={(task, next) => toggle.mutate({ taskId: task.id, done: next })}
          onApproveClaim={(task) => (task.claimedBy ? approve.mutate(task.claimedBy.completionId) : undefined)}
          byParent={isParent}
          viewerId={session?.memberId ?? null}
          emptyTitle="No household contributions set up"
        />
        <ErrorText error={toggle.error ?? approve.error} />
      </section>
    </div>
  )
}
